import { ArrowRight } from "lucide-react";

export type RelatedPageCard = {
  /** Internal route path, e.g. "/colorado-springs-seo". */
  to: string;
  /** Card title — should be descriptive, keyword-rich anchor text. */
  title: string;
  /** One-line description shown below the title. */
  description: string;
};

/**
 * "More from Colorado Web Impressions" — shared card grid surfaced on every
 * hub and spoke page. Replaces the older inline list-of-links pattern.
 *
 * Internal-linking rules satisfied per spec:
 *   • City hubs    → 3 city spoke cards + 3 industry hub cards (6 total)
 *   • Industry hubs → 3 industry spoke cards + 3 city hub cards (6 total)
 *   • Service hubs  → 3 city hub cards + 3 industry hub cards    (6 total)
 *   • Spokes        → parent hub card + 2 sibling spoke cards    (3 total)
 *   • Intersections → parent city hub + parent industry hub +
 *                     parent service hub                          (3 total)
 *
 * Anchor-text variation is the caller's responsibility — never repeat the
 * same `title` twice on the same page.
 */
export function RelatedPagesGrid({
  cards,
  heading = "More from Colorado Web Impressions",
  intro,
}: {
  cards: RelatedPageCard[];
  heading?: string;
  intro?: string;
}) {
  if (cards.length === 0) return null;

  return (
    <section
      aria-label={heading}
      className="border-y border-border bg-muted/30 py-16 lg:py-20"
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            {heading}
          </h2>
          {intro && (
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              {intro}
            </p>
          )}
        </div>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <li key={`${card.to}-${card.title}`}>
              <a
                href={card.to}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover"
              >
                <h3 className="text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                  {card.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {card.description}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                  Read more
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
